import { useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WebhookIcon from '@mui/icons-material/Webhook';
import SubscriptionsOverviewTab from './subscriptions/SubscriptionsOverviewTab';
import PaymentIssuesTab from './payment-issues/PaymentIssuesTab';
import RevenueAnalyticsTab from './revenue/RevenueAnalyticsTab';
import PaddleIntegrationTab from './paddle/PaddleIntegrationTab';
import { AdminBillingData } from '../../../types/admin';

type BillingTab = 'subscriptions' | 'payment-issues' | 'revenue' | 'paddle';

interface BillingTabsProps {
  data: AdminBillingData;
  loading?: boolean;
  onRefresh: () => void;
  onCancelSubscription: (subscriptionId: string, reason?: string) => Promise<{ success: boolean }>;
  onRetryPayment: (issueId: string) => Promise<{ success: boolean }>;
}

export default function BillingTabs({
  data,
  loading,
  onRefresh,
  onCancelSubscription,
  onRetryPayment,
}: BillingTabsProps) {
  const [activeTab, setActiveTab] = useState<BillingTab>('subscriptions');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: BillingTab) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<SubscriptionsIcon />} label="Subscriptions Overview" value="subscriptions" />
        <Tab icon={<ErrorIcon />} label="Payment Issues" value="payment-issues" />
        <Tab icon={<TrendingUpIcon />} label="Revenue Analytics" value="revenue" />
        <Tab icon={<WebhookIcon />} label="Paddle Integration" value="paddle" />
      </Tabs>

      {activeTab === 'subscriptions' && (
        <SubscriptionsOverviewTab
          metrics={data.metrics}
          subscriptions={data.subscriptions}
          loading={loading}
          lastUpdated={data.lastUpdated}
          onRefresh={onRefresh}
          onCancelSubscription={onCancelSubscription}
        />
      )}

      {activeTab === 'payment-issues' && (
        <PaymentIssuesTab
          metrics={data.paymentIssueMetrics}
          paymentIssues={data.paymentIssues}
          issueDistribution={data.paymentIssueDistribution}
          loading={loading}
          lastUpdated={data.lastUpdated}
          onRefresh={onRefresh}
          onRetryPayment={onRetryPayment}
        />
      )}

      {activeTab === 'revenue' && (
        <RevenueAnalyticsTab
          revenueMetrics={data.revenueMetrics}
          mrrBreakdown={data.mrrBreakdown}
          revenueTrend={data.revenueTrend}
          revenueByTier={data.revenueByTier}
          churnAnalysis={data.churnAnalysis}
          loading={loading}
          lastUpdated={data.lastUpdated}
          onRefresh={onRefresh}
        />
      )}

      {activeTab === 'paddle' && (
        <PaddleIntegrationTab
          paddleStatus={data.paddleStatus}
          recentEvents={data.recentWebhookEvents}
          eventDistribution={data.webhookEventDistribution}
          loading={loading}
          lastUpdated={data.lastUpdated}
          onRefresh={onRefresh}
        />
      )}
    </Box>
  );
}
